'use client'

import { ChevronRight, ChevronLeft } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import Image from 'next/image'
import LocalizedClientLink from '@modules/common/components/localized-client-link'
import { HttpTypes } from '@medusajs/types'
import { getProductPrice } from '@lib/util/get-product-price'

interface DopesnowHomeProps {
  categories: Array<{
    handle: string
    name: string
    image_url?: string
  }>
  products: HttpTypes.StoreProduct[]
  /** Full product pool for client-side re-shuffle (SHOP-85) */
  allProducts?: HttpTypes.StoreProduct[]
  region: HttpTypes.StoreRegion
}

// ─── Hero Section — 4 equal vertical columns, each with independent crossfade + zoom ──

const heroImagePools = [
  ['/images/hero-modern.jpg', '/images/categories/bags.jpg'],
  ['/images/categories/silk-scarf.jpg', '/images/categories/vests.jpg'],
  ['/images/categories/jewellery.jpg', '/images/categories/necklaces.jpg'],
  ['/images/categories/jackets.jpg', '/images/categories/earrings.jpg'],
]

function HeroSection() {
  const [indices, setIndices] = useState([0, 0, 0, 0])

  useEffect(() => {
    const timers = [
      setInterval(() => setIndices(prev => { const n = [...prev]; n[0] = (n[0] + 1) % heroImagePools[0].length; return n }), 4000),
      setInterval(() => setIndices(prev => { const n = [...prev]; n[1] = (n[1] + 1) % heroImagePools[1].length; return n }), 5500),
      setInterval(() => setIndices(prev => { const n = [...prev]; n[2] = (n[2] + 1) % heroImagePools[2].length; return n }), 3500),
      setInterval(() => setIndices(prev => { const n = [...prev]; n[3] = (n[3] + 1) % heroImagePools[3].length; return n }), 6000),
    ]
    return () => timers.forEach(clearInterval)
  }, [])

  return (
    <section className="mx-4 md:mx-10 mt-2">
      <style jsx>{`
        @keyframes heroZoom {
          from { transform: scale(1); }
          to { transform: scale(1.05); }
        }
      `}</style>
      {/* 4 equal vertical columns */}
      <div className="grid grid-cols-4 gap-[2px] h-[500px] md:h-[600px]">
        {heroImagePools.map((pool, i) => (
          <div key={i} className="relative overflow-hidden bg-[#F5F5F5]">
            {pool.map((img, imgIdx) => (
              <Image
                key={img}
                src={img}
                alt={`Hero ${i}-${imgIdx}`}
                fill
                className={`object-cover transition-opacity duration-1000 ${imgIdx === indices[i] ? 'opacity-100' : 'opacity-0'}`}
                style={imgIdx === indices[i] ? { animation: 'heroZoom 7s ease-out forwards' } : undefined}
                priority={i === 0 && imgIdx === 0}
              />
            ))}
          </div>
        ))}
      </div>
      {/* CTA overlay */}
      <div className="relative -mt-20 ml-8 z-10 pb-8">
        <h1 className="text-3xl md:text-4xl font-black text-black bg-white/90 inline-block px-4 py-2 mb-2">
          New Collection
        </h1>
        <div className="flex gap-2">
          <LocalizedClientLink
            href="/categories/jewellery"
            className="bg-white/90 text-black text-sm font-semibold px-4 py-2 flex items-center gap-1 hover:bg-white"
          >
            Jewellery <ChevronRight className="w-4 h-4" />
          </LocalizedClientLink>
          <LocalizedClientLink
            href="/categories/clothing-vip"
            className="bg-white/90 text-black text-sm font-semibold px-4 py-2 flex items-center gap-1 hover:bg-white"
          >
            Clothing <ChevronRight className="w-4 h-4" />
          </LocalizedClientLink>
        </div>
      </div>
    </section>
  )
}

// ─── Categories Section — First category spans 2 rows ─────────────────────────

function CategoriesSection({ categories }: { categories: DopesnowHomeProps['categories'] }) {
  const withImage = categories.filter((cat) => cat.image_url)
  // Take exactly enough to fill the grid without empty cells
  // With first spanning 2 rows in a 4-col grid: row1 has 3 normal + 1 tall, row2 has 3 normal (tall continues)
  // So 1 tall + 6 normal = 7 categories fills a 4x2 grid perfectly
  const display = withImage.slice(0, 7)

  return (
    <section className="px-4 md:px-10 mb-16 mt-8">
      <h2 className="text-xl font-bold mb-4">Top Categories</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-[2px] auto-rows-[220px]">
        {display.map((cat, i) => (
          <LocalizedClientLink
            key={cat.handle}
            href={`/categories/${cat.handle}`}
            className={`relative overflow-hidden group bg-[#F5F5F5] ${i === 0 ? 'row-span-2' : ''}`}
          >
            <Image
              src={cat.image_url!}
              alt={cat.name}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <span className="absolute bottom-3 left-3 text-xs font-medium text-black bg-white/80 px-2 py-1">
              {cat.name}
            </span>
          </LocalizedClientLink>
        ))}
      </div>
    </section>
  )
}

// ─── Products Grid — Image carousel on each card ──────────────────────────────

function ProductImageStatic({ images, alt }: { images: string[], alt: string }) {
  return (
    <div className="relative aspect-[340/420] bg-[#F5F5F5] overflow-hidden">
      <Image
        src={images[0]}
        alt={alt}
        fill
        className="object-cover"
      />
    </div>
  )
}

function diversifyProducts(products: HttpTypes.StoreProduct[]): HttpTypes.StoreProduct[] {
  if (products.length === 0) return products
  const byCategory = new Map<string, HttpTypes.StoreProduct[]>()
  for (const p of products) {
    const catId = p.categories?.[0]?.id || 'uncategorized'
    if (!byCategory.has(catId)) byCategory.set(catId, [])
    byCategory.get(catId)!.push(p)
  }
  const result: HttpTypes.StoreProduct[] = []
  const seen = new Set<string>()
  const groups = Array.from(byCategory.values())
  const indices = groups.map(() => 0)
  while (result.length < 8 && result.length < products.length) {
    let added = false
    for (let g = 0; g < groups.length; g++) {
      if (indices[g] < groups[g].length) {
        const p = groups[g][indices[g]]
        indices[g]++
        if (!seen.has(p.id)) {
          seen.add(p.id)
          result.push(p)
          added = true
          if (result.length >= 8) break
        }
      }
    }
    if (!added) break
  }
  return result
}

function ProductsSection({ products, region }: { products: HttpTypes.StoreProduct[], region: HttpTypes.StoreRegion }) {
  const diversified = diversifyProducts(products)

  return (
    <section className="px-4 md:px-10 mb-16">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Featured Products</h2>
        <LocalizedClientLink href="/store" className="flex items-center gap-1 text-sm font-semibold hover:opacity-70">
          View all <ChevronRight className="w-5 h-5" />
        </LocalizedClientLink>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-[2px]">
        {diversified.slice(0, 8).map((product) => {
          const { cheapestPrice } = getProductPrice({ product, region })
          // Collect all product images for carousel
          const allImages = [
            product.thumbnail,
            ...(product.images?.map(img => img.url) || []),
          ].filter(Boolean) as string[]
          const images = allImages.length > 0 ? allImages.slice(0, 4) : ['/images/placeholder.jpg']

          return (
            <LocalizedClientLink key={product.id} href={`/products/${product.handle}`} className="group">
              <ProductImageStatic images={images} alt={product.title || ''} />
              <div className="mt-2 px-1 pb-4">
                <p className="text-sm font-medium text-black truncate">{product.title}</p>
                {cheapestPrice && <span className="text-sm font-bold text-black">{cheapestPrice.calculated_price}</span>}
              </div>
            </LocalizedClientLink>
          )
        })}
      </div>
    </section>
  )
}

// ─── Reviews Section ──────────────────────────────────────────────────────────

const reviews = [
  { rating: 5, title: "Beautiful silk scarf! The quality is amazing and colours are vibrant.", author: "Sarah M.", verified: true, product: "Silk Scarf", price: "$89" },
  { rating: 5, title: "Love this jade bracelet. Perfect gift for my mum's birthday!", author: "Jennifer L.", verified: true, product: "Jade Bracelet", price: "$129" },
  { rating: 5, title: "Stunning earrings! Get so many compliments every time I wear them.", author: "Michelle K.", verified: true, product: "Pearl Earrings", price: "$79" },
  { rating: 5, title: "Fast shipping to Sydney. The packaging was beautiful too!", author: "David W.", verified: true, product: "Brocade Jacket", price: "$189" },
]

function ReviewsSection() {
  const scrollRef = useRef<HTMLDivElement>(null)
  const scroll = (dir: 'left' | 'right') => {
    scrollRef.current?.scrollBy({ left: dir === 'left' ? -300 : 300, behavior: 'smooth' })
  }

  return (
    <section className="px-4 md:px-10 mb-16">
      <h2 className="text-lg font-bold mb-1">What our customers are saying</h2>
      <p className="text-xs text-gray-500 mb-4">Based on <span className="font-bold">4.8</span>/5 from 500+ happy customers</p>
      <div className="relative">
        <div ref={scrollRef} className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
          {reviews.map((review, i) => (
            <div key={i} className="flex-shrink-0 w-[280px] border border-gray-200 p-5 bg-white">
              <div className="flex gap-0.5 mb-2">
                {Array.from({ length: review.rating }).map((_, j) => (
                  <span key={j} className="text-black text-sm">★</span>
                ))}
              </div>
              <p className="text-sm mb-4 line-clamp-2 font-medium">&ldquo;{review.title}&rdquo;</p>
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                <span className="font-medium text-black">{review.author}</span>
                {review.verified && <span className="text-green-600">✓ Verified buyer</span>}
              </div>
              <div className="border-t border-gray-100 pt-3">
                <p className="text-xs font-medium">{review.product}</p>
                <p className="text-xs text-gray-500">{review.price}</p>
              </div>
            </div>
          ))}
        </div>
        <button onClick={() => scroll('left')} className="absolute -left-3 top-1/2 -translate-y-1/2 bg-white shadow-md w-8 h-8 flex items-center justify-center">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button onClick={() => scroll('right')} className="absolute -right-3 top-1/2 -translate-y-1/2 bg-white shadow-md w-8 h-8 flex items-center justify-center">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  )
}

// ─── Newsletter Section ───────────────────────────────────────────────────────

function NewsletterSection() {
  return (
    <section className="px-4 md:px-10 mb-16 text-center">
      <h2 className="text-2xl font-bold mb-2">Stay in the loop</h2>
      <p className="text-sm text-gray-500 mb-6">Subscribe for exclusive offers and new arrivals.</p>
      <form className="flex max-w-md mx-auto">
        <input type="email" placeholder="Enter your email" className="flex-1 border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black" />
        <button type="submit" className="bg-black text-white px-6 py-3 text-sm font-semibold hover:bg-gray-800">Subscribe</button>
      </form>
      <p className="text-xs text-gray-400 mt-4">Free shipping on all orders over $150</p>
    </section>
  )
}

// ─── Main Export ──────────────────────────────────────────────────────────────

export function DopesnowHome({ categories, products, allProducts, region }: DopesnowHomeProps) {
  const pool = allProducts && allProducts.length > products.length ? allProducts : null
  const [displayedProducts, setDisplayedProducts] = useState(products)
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    if (!pool) return
    setVisible(false)
    const t = setTimeout(() => {
      const arr = [...pool]
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]]
      }
      setDisplayedProducts(arr.slice(0, products.length))
      setVisible(true)
    }, 150)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="max-w-[1400px] mx-auto">
      <HeroSection />
      <CategoriesSection categories={categories} />
      <div style={{ transition: "opacity 0.15s ease-in-out", opacity: visible ? 1 : 0 }}>
        <ProductsSection products={displayedProducts} region={region} />
      </div>
      <ReviewsSection />
      <NewsletterSection />
    </div>
  )
}
